import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Check, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { STATUSES, newId, sendEntries, errorText, toMonthInput } from '../../lib/service';
import { ItemsTable, EntryList, StatusBadge, formatDate } from './ServiceHistory';

// Suojatilakortin huolto-osat (#82 versio 6, 29.10.2026): tarvikkeet Suojatilan tiedot -kenttään,
// huoltokirjaus (tila OK/NOT ja muut huomiot) ja historia Huolto-kenttään. Tarvikkeen nimi valitaan
// aiemmin käytetyistä tai kirjoitetaan uusi, jotta sama tarvike on Huolto-sivun listassa
// yksi sarake (päätös 32). Tarvikemuutokset eivät merkitse suojatilaa huolletuksi.

const inputCls = 'w-full rounded-lg border border-gray-300 text-sm px-3 py-2.5 focus:outline-none focus:border-brand-500';
const btnPrimary = 'inline-flex items-center justify-center gap-1.5 bg-brand-700 hover:bg-brand-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed';
const btnSmall = 'text-xs font-medium px-2.5 py-1.5 rounded-lg border';
const NAMES_LIST_ID = 'service-item-names';

// Otsikko sidotaan kenttään ympäröivällä labelilla (ruudunlukijat ja automaatio näkevät nimen).
function Field({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

// Avattava osio (tarvikkeet, historia).
function Section({ title, open, onToggle, children, action }) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button type="button" onClick={onToggle} aria-expanded={open}
          className="flex-1 flex items-center gap-1.5 text-left text-sm font-medium text-gray-800">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />} {title}
        </button>
        {action}
      </div>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

export function StatusPicker({ value, onChange }) {
  const { t } = useTranslation();
  const active = { ok: 'bg-green-600 text-white border-green-600', not: 'bg-red-600 text-white border-red-600' };
  return (
    <div className="flex gap-2" role="radiogroup" aria-label={t('service.statusLabel')}>
      {STATUSES.map(s => (
        <button
          key={s}
          type="button"
          role="radio"
          aria-checked={value === s}
          onClick={() => onChange(s)}
          className={`flex-1 sm:flex-none px-4 py-2.5 rounded-lg border text-sm font-semibold ${value === s ? active[s] : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >{t(`service.status.${s}`)}</button>
      ))}
    </div>
  );
}

function ServiceForm({ shelterId, onSend, onSaved }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSend([{
        id: newId(), shelter_id: shelterId, type: 'service', performed_at: new Date().toISOString(),
        payload: { status, notes: notes.trim() || null },
      }]);
      await onSaved();
    } catch (err) {
      setError(errorText(err, t));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">{t('service.statusLabel')}</p>
        <StatusPicker value={status} onChange={setStatus} />
      </div>

      <Field label={t('service.otherNotes')}>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} maxLength={4000} rows={3}
          placeholder={t('service.otherNotesPlaceholder')} className={inputCls} />
      </Field>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy || !status} className={btnPrimary}>
          <Check size={14} /> {busy ? t('common.saving') : t('service.saveService')}
        </button>
        {!status && <p className="text-xs text-gray-500">{t('service.statusHint')}</p>}
      </div>
    </form>
  );
}

// Aiemmin käytetyt tarvikenimet valikoksi: nimen voi valita tai kirjoittaa uuden.
function ItemNames({ names }) {
  return (
    <datalist id={NAMES_LIST_ID}>
      {names.map(n => <option key={n} value={n} />)}
    </datalist>
  );
}

const emptyItem = { name: '', quantity: '', model: '', month: '' };

function AddItemForm({ shelterId, onSend, onDone }) {
  const { t } = useTranslation();
  const [f, setF] = useState(emptyItem);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await onSend([{
        id: newId(), shelter_id: shelterId, type: 'item_add',
        payload: {
          item_id: newId(),
          name: f.name.trim(),
          quantity: f.quantity === '' ? null : Number(f.quantity),
          model: f.model.trim() || null,
          expires_on: f.month || null,
        },
      }]);
      setF(emptyItem);
      onDone();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t('service.itemName')} className="sm:col-span-2">
          <input value={f.name} onChange={e => set('name', e.target.value)} maxLength={200} list={NAMES_LIST_ID}
            placeholder={t('service.itemNamePlaceholder')} className={inputCls} autoFocus />
        </Field>
        <Field label={t('service.quantity')}>
          <input type="number" inputMode="decimal" min="0" step="any" value={f.quantity} onChange={e => set('quantity', e.target.value)} className={inputCls} />
        </Field>
        <Field label={t('service.model')}>
          <input value={f.model} onChange={e => set('model', e.target.value)} maxLength={100} placeholder={t('service.modelPlaceholder')} className={inputCls} />
        </Field>
        <Field label={t('service.expiresMonth')}>
          <input type="month" value={f.month} onChange={e => set('month', e.target.value)} className={inputCls} />
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">{t('common.cancel')}</button>
        <button type="submit" disabled={busy || !f.name.trim()} className={btnPrimary}>
          <Check size={14} /> {busy ? t('common.saving') : t('service.addItem')}
        </button>
      </div>
    </form>
  );
}

// Tarvikkeen toiminnot: muokkaus, vaihdettavaksi, vaihdettu (uusi kuukausi pakollinen), poisto.
function ItemActions({ item, onSend }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // 'edit' | 'replaced' | 'remove'
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function open(m) {
    setError('');
    setMode(m);
    setF({
      name: item.name,
      quantity: item.quantity != null ? String(Number(item.quantity)) : '',
      model: item.model || '',
      month: m === 'replaced' ? '' : toMonthInput(item.expires_on),
    });
  }

  async function send(type, payload) {
    setBusy(true); setError('');
    try {
      await onSend([{ id: newId(), shelter_id: item.shelter_id, type, payload: { item_id: item.id, ...payload } }]);
      setMode(null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  }

  const qty = f.quantity === '' ? null : Number(f.quantity);
  // Vain muuttuneet kentät, jotta historia kertoo, mitä todella muutettiin.
  function changedFields() {
    const next = { name: (f.name || '').trim(), quantity: qty, model: (f.model || '').trim() || null, expires_on: f.month || null };
    const prev = {
      name: item.name,
      quantity: item.quantity != null ? Number(item.quantity) : null,
      model: item.model || null,
      expires_on: toMonthInput(item.expires_on) || null,
    };
    return Object.fromEntries(Object.entries(next).filter(([k, v]) => v !== prev[k]));
  }

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => open('edit')} className={`${btnSmall} border-gray-300 text-gray-700 hover:bg-gray-100`}>{t('common.edit')}</button>
        {!item.marked_for_replacement && (
          <button onClick={() => send('item_mark_replacement', {})} disabled={busy} className={`${btnSmall} border-amber-300 text-amber-800 hover:bg-amber-50`}>
            {t('service.markReplacement')}
          </button>
        )}
        <button onClick={() => open('replaced')} className={`${btnSmall} border-green-300 text-green-800 hover:bg-green-50`}>{t('service.markReplaced')}</button>
        <button onClick={() => open('remove')} className={`${btnSmall} border-red-200 text-red-700 hover:bg-red-50`}>{t('common.delete')}</button>
        {error && <p className="w-full text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="w-full mt-2 border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
      {mode === 'remove' ? (
        <p className="text-sm text-gray-700">{t('service.removeConfirm', { name: item.name })}</p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {mode === 'edit' && (
            <Field label={t('service.itemName')} className="col-span-2">
              <input value={f.name} onChange={e => setF(p => ({ ...p, name: e.target.value }))} maxLength={200} list={NAMES_LIST_ID} className={inputCls} />
            </Field>
          )}
          <Field label={t('service.quantity')}>
            <input type="number" inputMode="decimal" min="0" step="any" value={f.quantity} onChange={e => setF(p => ({ ...p, quantity: e.target.value }))} className={inputCls} />
          </Field>
          {mode === 'edit' ? (
            <Field label={t('service.model')}>
              <input value={f.model} onChange={e => setF(p => ({ ...p, model: e.target.value }))} maxLength={100} className={inputCls} />
            </Field>
          ) : <div />}
          <Field label={mode === 'replaced' ? t('service.newExpiresOn') : t('service.expiresMonth')} className="col-span-2">
            <input type="month" value={f.month} onChange={e => setF(p => ({ ...p, month: e.target.value }))} className={inputCls} />
          </Field>
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button onClick={() => setMode(null)} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-2">{t('common.cancel')}</button>
        {mode === 'edit' && (
          <button disabled={busy || !f.name.trim() || Object.keys(changedFields()).length === 0}
            onClick={() => send('item_update', changedFields())} className={btnPrimary}>{t('common.save')}</button>
        )}
        {mode === 'replaced' && (
          <button disabled={busy || !f.month} onClick={() => send('item_replaced', { expires_on: f.month, quantity: qty })} className={btnPrimary}>
            {t('service.markReplaced')}
          </button>
        )}
        {mode === 'remove' && (
          <button disabled={busy} onClick={() => send('item_remove', {})} className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-60">
            {t('common.delete')}
          </button>
        )}
      </div>
    </div>
  );
}

// Suojatilan huoltodata (/api/service/shelters/:id/history) kortin molemmille kentille:
// Suojatilan tiedot (tarvikkeet) ja Huolto (kirjaus ja historia). enabled = false, kun
// käyttäjä ei näe huoltohistoriaa (Admin ilman Superadminin lupaa).
export function useShelterService(shelterId, enabled) {
  const { profile } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!enabled) return;
    setError('');
    try {
      const { data: d } = await api.get(`/api/service/shelters/${shelterId}/history?limit=100`);
      setData(d);
    } catch (err) {
      setError(err.response?.data?.error || t('common.loadError'));
    }
  }, [shelterId, enabled, t]);

  useEffect(() => { reload(); }, [reload]);

  const send = useCallback(entries => sendEntries(entries, profile?.id), [profile?.id]);
  return { data, error, reload, send };
}

// Tarvikkeet (Suojatilan tiedot -kenttä). canEdit: Superadmin lisää, muokkaa ja poistaa.
export function ShelterItems({ service, canEdit }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const { data, error, reload, send } = service;

  // Tarvikkeen muutos: tiedot päivitetään (myös Huolto-kentän historia).
  const sendItem = useCallback(async (entries) => {
    await send(entries);
    await reload();
  }, [send, reload]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-sm text-gray-400">{t('common.loading')}</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-medium text-gray-800">{t('service.itemsSection', { count: data.items.length })}</h3>
        {canEdit && !adding && (
          <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-1 text-sm text-brand-700 hover:text-brand-900 font-medium">
            <Plus size={14} /> {t('service.addItem')}
          </button>
        )}
      </div>
      {canEdit && <ItemNames names={data.item_names || []} />}
      {canEdit && adding && (
        <div className="mb-3">
          <AddItemForm shelterId={data.shelter.id} onSend={sendItem} onDone={() => setAdding(false)} />
        </div>
      )}
      <ItemsTable items={data.items} renderActions={canEdit ? item => <ItemActions item={item} onSend={sendItem} /> : undefined} />
    </div>
  );
}

// Huoltokirjaus (Huolto-kenttä): viimeisin huolto ja, Superadminilla, uusi kirjaus.
// onSaved: kortti voi päivittää muut osionsa (esim. viimeisin huolto perustiedoissa).
export function ServiceRecord({ service, canEdit, onSaved }) {
  const { t, i18n } = useTranslation();
  const [formKey, setFormKey] = useState(0);
  const [savedMsg, setSavedMsg] = useState('');
  const { data, error, reload, send } = service;

  const handleSaved = useCallback(async () => {
    await reload();
    setFormKey(k => k + 1);
    setSavedMsg(t('service.serviceSaved'));
    onSaved?.();
  }, [reload, t, onSaved]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <p className="text-sm text-gray-400">{t('common.loading')}</p>;

  const last = data.last_service;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
        {last ? (
          <>
            <span>{t('service.lastServiceBy', { date: formatDate(last.performed_at, i18n.language), by: last.performed_by_email || '—' })}</span>
            <StatusBadge status={last.status} />
          </>
        ) : <span>{t('service.neverServiced')}</span>}
      </div>
      {last?.notes && <p className="text-sm text-gray-700 whitespace-pre-line bg-gray-50 rounded-lg px-3 py-2">{last.notes}</p>}
      {savedMsg && <p className="text-sm text-green-700 font-medium flex items-center gap-1"><Check size={14} /> {savedMsg}</p>}
      {canEdit && <ServiceForm key={formKey} shelterId={data.shelter.id} onSend={send} onSaved={handleSaved} />}
    </div>
  );
}

// Huoltohistoria avattavana osiona (oletuksena kiinni).
export function ServiceHistoryList({ service }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data } = service;
  if (!data) return null;
  return (
    <Section title={t('service.history')} open={open} onToggle={() => setOpen(o => !o)}>
      <EntryList entries={data.entries} items={data.items} />
    </Section>
  );
}
